import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { PageEvent } from '@angular/material';
import { Router } from '@angular/router';
import { SatPopover } from '@ncstate/sat-popover';

import { FboResult } from '../../shared/models/fbo-result.model';
import { State } from '../../shared/state-view/state.model';
import { SearchQuery } from '../models/search-query.model';
import { storageAvailable } from '../../util/helpers';

/**
 * Results list for the Business Finder (FBOs), mirroring {@link FlightSchoolFinderResultsListingComponent}.
 * Renders the paged result cards (each linking to the existing `/business/:id` detail page), a filter
 * popover, and relays hover events for map sync.
 */
@Component({
  selector: 'app-fbo-finder-results-listing',
  templateUrl: './fbo-finder-results-listing.component.html',
  styleUrls: ['./fbo-finder-results-listing.component.scss']
})
export class FboFinderResultsListingComponent {
  @Input() query: SearchQuery;
  @Input() fbos: Array<FboResult>;
  @Input() state: State = State.Empty;
  @Input() activeFbo: FboResult;
  @Input() errorMessage: string;
  @Input() pageIndex = 0;
  @Input() pageSize = 25;
  @Input() totalFbos = 0;
  @Output() fboMouseEnter = new EventEmitter<FboResult>();
  @Output() fboMouseLeave = new EventEmitter<FboResult>();
  @Output() filtersChanged = new EventEmitter<SearchQuery>();
  @Output() pageChanged = new EventEmitter<PageEvent>();

  @ViewChild('fboListEl') fboListEl: ElementRef;
  @ViewChildren('fboEl', { read: ElementRef }) fboEls: QueryList<ElementRef>;
  @ViewChild('fboFiltersPopover') fboFiltersPopover: SatPopover;

  constructor(private _router: Router) {}

  /**
   * Records the current search results URL before navigating to an FBO's detail page, so that page's "Back"
   * button can return to these exact results (see BusinessPageComponent.goBack). Also clears the airport
   * back-target so the FBO opened from search doesn't fall back to "Back to <airport>".
   */
  onFboSelected() {
    if (storageAvailable('sessionStorage')) {
      sessionStorage.setItem('businessFinderSearchReturn', this._router.url);
      sessionStorage.removeItem('lastAirport');
    }
  }

  onFboHover(fbo: FboResult, hover: boolean) {
    if (hover) {
      this.fboMouseEnter.emit(fbo);
    } else {
      this.fboMouseLeave.emit(fbo);
    }
  }

  onFiltersChanged() {
    this.filtersChanged.emit(this.query);
  }

  /**
   * Returns a jQuery element container for the given FBO.
   */
  getFboEl(fbo: FboResult) {
    const i = this.fbos.indexOf(fbo);
    if (i < 0) {
      return null;
    }
    const el = this.fboEls.toArray()[i];
    // @ts-ignore
    return $(el.nativeElement);
  }

  /**
   * Scrolls the list to the specified FBO (used for map hover sync).
   */
  public scrollToFbo(fbo: FboResult) {
    if (!this.fboListEl) {
      return;
    }
    // @ts-ignore
    const $containerEl = $(this.fboListEl.nativeElement);
    const $el = this.getFboEl(fbo);
    if (!$el) {
      return;
    }
    const position = $el[0].offsetTop;
    $containerEl.stop(true);
    $containerEl.animate({ scrollTop: position }, 500);
  }

  onPageChanged(e: PageEvent) {
    this.pageChanged.emit(e);
    if (!this.fboListEl) {
      return;
    }
    // @ts-ignore
    const $containerEl = $(this.fboListEl.nativeElement);
    $containerEl.stop(true);
    $containerEl.animate({ scrollTop: 0 }, 500);
  }
}
